/**
 * Configuration Service
 * Provides centralized access to application configuration
 */

import { config, isDev, isProd, isStagingEnv, isDebugEnabled } from '../config/environment.js'
import { API_CONFIG } from '../config/api.js'

class ConfigService {
  constructor() {
    this.config = config
    this.apiConfig = API_CONFIG
  }

  /**
   * Get current environment
   */
  get environment() {
    return this.config.app.environment || 'development'
  }

  /**
   * Get app name
   */
  get appName() {
    return this.config.app.name
  }

  /**
   * Get app version
   */
  get appVersion() {
    return this.config.app.version
  }

  /**
   * Check if debug is enabled
   */
  get isDebugEnabled() {
    return isDebugEnabled()
  }

  /**
   * Check if running in development
   */
  get isDevelopment() {
    return isDev()
  }

  /**
   * Check if running in production
   */
  get isProduction() {
    return isProd()
  }

  /**
   * Check if running in staging
   */
  get isStaging() {
    return isStagingEnv()
  }

  /**
   * Get Web API URL
   */
  get webApiUrl() {
    return this.apiConfig.WEB_API.BASE_URL
  }

  /**
   * Get Mobile API URL
   */
  get mobileApiUrl() {
    return this.apiConfig.MOBILE_API.BASE_URL
  }

  /**
   * Get Storage API URL
   */
  get storageApiUrl() {
    return this.apiConfig.STORAGE.BASE_URL
  }

  /**
   * Get Translation API URL
   */
  get translationApiUrl() {
    return this.apiConfig.TRANSLATION.BASE_URL
  }

  /**
   * Get API timeout
   */
  get apiTimeout() {
    return this.config.api.timeout
  }

  /**
   * Get API headers
   */
  get apiHeaders() {
    return this.config.api.headers
  }

  /**
   * Check if a feature is enabled
   */
  isFeatureEnabled(feature) {
    return this.config.features[feature] || false
  }

  /**
   * Get full configuration object
   */
  getFullConfig() {
    return {
      environment: this.environment,
      app: this.config.app,
      api: this.apiConfig,
      features: this.config.features,
      isDevelopment: this.isDevelopment,
      isProduction: this.isProduction,
      isStaging: this.isStaging,
    }
  }

  /**
   * Log configuration (only in development)
   */
  logConfig() {
    if (this.isDevelopment) {
      console.log('🔧 ConfigService Configuration:', this.getFullConfig())
    }
  }

  /**
   * Get environment info for debugging
   */
  getEnvironmentInfo() {
    return {
      environment: this.environment,
      appName: this.appName,
      appVersion: this.appVersion,
      webApiUrl: this.webApiUrl,
      mobileApiUrl: this.mobileApiUrl,
      isDebugEnabled: this.isDebugEnabled,
      isDevelopment: this.isDevelopment,
      isProduction: this.isProduction,
      isStaging: this.isStaging,
    }
  }
}

// Create singleton instance
const configService = new ConfigService()

// Log configuration on initialization (only in development)
configService.logConfig()

export default configService 